import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider as HSDThemeProvider } from '@hopskipdrive/react-components';
import { LicenseInfo } from '@mui/x-license-pro';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { AxiosErrorBoundary } from './api/utils/AxiosErrorBoundary';
import { useAuthBootstrap } from './auth/hooks/useAuthBootstrap';
import { useCrossTabAuthSync } from './auth/hooks/useCrosssTabAuthSync';
import { useCsrf } from './auth/hooks/useCsrf';
import { AbilityProvider } from './contexts/AbilityContext';
import { LaunchDarklyProvider } from './contexts/LDContext';
import { useAnalyticsIdentify } from './hooks/useAnalyticsIdentify';
import { useClearToasts } from './hooks/useClearToasts';
import { useNetworkStatus } from './hooks/useNetworkStatus';
import { useRefiner } from './hooks/useRefiner';
import { theme, ThemeProvider as RideIQThemeProvider } from './shared/components/theme';
import { ToastRenderer } from './shared/components/ToastRenderer/ToastRenderer';
import { LiveRegionAnnouncerProvider } from './shared/contexts/LiveRegionAnnouncerProvider';
import { queryClient } from './shared/queryClient';
import { ErrorBoundary } from './utils/errorReporting/ErrorBoundary';
import { useChat } from './vendor/features/Chat';
import { useFullStory } from './vendor/features/FullStory/useFullStory';
import { usePendo } from './vendor/features/Pendo/usePendo';
import Routes from './routes';

LicenseInfo.setLicenseKey(window.env?.RIDEIQ_MUI_PRO_LICENSE_KEY);

export const App = (): JSX.Element => {
  useCrossTabAuthSync();

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <LiveRegionAnnouncerProvider>
          <QueryClientProvider client={queryClient}>
            <AuthBootstrap />
            <AxiosErrorBoundary>
              <LaunchDarklyProvider>
                <AbilityProvider>
                  <HSDThemeProvider customTheme={theme}>
                    <RideIQThemeProvider>
                      <ToastRenderer />
                      <Services>
                        <Routes />
                      </Services>
                    </RideIQThemeProvider>
                  </HSDThemeProvider>
                </AbilityProvider>
              </LaunchDarklyProvider>
            </AxiosErrorBoundary>
            {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
          </QueryClientProvider>
        </LiveRegionAnnouncerProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

const Services = ({ children }) => {
  useChat();
  useClearToasts();
  useAnalyticsIdentify();
  useRefiner();
  useNetworkStatus();
  usePendo();
  useFullStory();
  useCsrf();

  return children;
};

const AuthBootstrap = () => {
  useAuthBootstrap();

  return null;
};
